import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { DynamicContainer, Flex, Icon } from 'components'
import BodyPortal from 'components/BodyPortal'
import RightPanel from 'components/RightPanel'
import Text from 'components/Text'

import { leadDetailRootId } from 'modules/Leads/LeadDetail'
import { ContactInfo } from 'store/types/lead'
import useLeadDetail from 'hooks/use-lead-detail'
import useContactInfo from 'hooks/use-contact-info'
import { LeadDetails } from 'types/global'
import { ThemeColor, colors } from 'utils/colors'

import MobileContactTable from './MobileContactTable'
import { DialerClientStatus } from 'modules/DialerClient/DialerUIContext'
import { auto } from '@popperjs/core'

interface Props {
  defaultContact?: ContactInfo
  onClose: () => void
  onSubmit: (leadId: string, contactInfo: ContactInfo) => void
  isSubmitting: boolean
  disableSubmit: boolean
  dialerClientStatus: DialerClientStatus
}

const LiveCallPanelV2: React.FC<Props> = ({
  defaultContact,
  onClose,
  onSubmit,
  isSubmitting,
  disableSubmit,
  dialerClientStatus,
}) => {
  const { leadId } = useLeadDetail()
  const { mobileContactInfos } = useContactInfo({ leadId })

  const leadDetailRef = useRef(document.getElementById(leadDetailRootId))
  const [contactInfo, setContactInfo] = useState<ContactInfo | undefined>(defaultContact)

  useEffect(() => {
    if (mobileContactInfos && mobileContactInfos.length > 0) {
      const firstContact = mobileContactInfos[0]
      const firstContactNumber = firstContact?.mobile?.[0]
      const hasSingleOption = mobileContactInfos?.length === 1 && firstContact?.mobile?.length === 1

      if (!defaultContact && hasSingleOption && firstContactNumber && firstContactNumber.id) {
        setContactInfo({
          contactInfoId: firstContactNumber.id,
          fname: firstContact.fname,
          lname: firstContact.lname,
          contactInfoTypeId: firstContactNumber.contactInfoTypeId,
          contactValue: firstContactNumber.value,
          isPrimaryContact: firstContactNumber.isPrimary,
        })
      }
    }
  }, [defaultContact, mobileContactInfos])

  const handleSelect = useCallback((value: LeadDetails.Mobile) => {
    if (!value.id) {
      return
    }

    setContactInfo({
      ...value,
      contactInfoId: value.id,
      contactValue: value.value,
      isPrimaryContact: value.isPrimary,
    })
  }, [])

  const selectedMobile: LeadDetails.Mobile | undefined = useMemo(() => {
    const contact = mobileContactInfos?.find((contact) =>
      contact.mobile?.find((mobile) => mobile.id === contactInfo?.contactInfoId),
    )

    if (contact) {
      return contact.mobile?.find((mobile) => mobile.id === contactInfo?.contactInfoId)
    }

    return
  }, [mobileContactInfos, contactInfo])

  const handleSubmit = useCallback(() => {
    if (leadId && contactInfo) {
      onSubmit(leadId, contactInfo)
    }
  }, [onSubmit, leadId, contactInfo])

  return (
    <BodyPortal rootNode={leadDetailRef.current}>
      <RightPanel
        isSubmitting={isSubmitting}
        disableSecondaryOnSubmitting={false}
        onCancel={onClose}
        onSubmit={handleSubmit}
        submitLabel="call_lbl"
        title="live_call_title"
        width={540}
        disableSubmit={disableSubmit || !contactInfo}
        addonFooter={<DialerStatus dialerClientStatus={dialerClientStatus} />}
      >
        <Text mb="1rem">live_call_select_contact</Text>
        <DynamicContainer offsetBottom={110}>
          <MobileContactTable options={mobileContactInfos} value={selectedMobile} onChange={handleSelect} />
        </DynamicContainer>
      </RightPanel>
    </BodyPortal>
  )
}

const getConfigs = (
  dialerClientStatus: DialerClientStatus,
): {
  icon: string[] | null
  message: string
  color: ThemeColor
  backgroundColor: ThemeColor
} => {
  switch (dialerClientStatus.status) {
    case 'connecting':
      return { icon: null, message: 'connecting', color: 'grayShadeBlack2', backgroundColor: 'white' }
    case 'ready':
      return {
        icon: ['far', 'circle-check'],
        message: 'READY',
        color: 'green2',
        backgroundColor: 'backgroundGreen',
      }
    case 'dialer_conncting':
      return {
        icon: ['far', 'info-circle'],
        message: 'dialer_conncting',
        color: 'gray8',
        backgroundColor: 'gray1',
      }
    case 'error':
      return {
        icon: ['far', 'exclamation-circle'],
        message: dialerClientStatus.message || 'unknown_error_lbl',
        color: 'red',
        backgroundColor: 'backgroundRed',
      }
  }
}

const DialerStatus = ({ dialerClientStatus }: Pick<Props, 'dialerClientStatus'>) => {
  const { icon, message, color, backgroundColor } = getConfigs(dialerClientStatus)
  return (
    <Flex
      justifyContent="space-between"
      px="1rem"
      py="1rem"
      backgroundColor={backgroundColor}
      borderTop={`1px solid ${colors.gray4}`}
      minHeight={40}
      maxHeight={150}
      overflowY={auto}
    >
      <Flex gap="0.5rem">
        {icon && <Icon icon={icon} color={color} />}
        <Text color={color} whiteSpace="pre-line">
          {message}
        </Text>
      </Flex>
      {dialerClientStatus.status == 'ready' && (
        <Text fontSize="12px" color={color}>
          {dialerClientStatus.version && `Version: ${dialerClientStatus.version}`}
        </Text>
      )}

      {dialerClientStatus.status == 'error' && dialerClientStatus.actions && dialerClientStatus.actions}
    </Flex>
  )
}

export default LiveCallPanelV2
